'use client';

import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import bannerStyles from './bannerStyles.module.css';

import { cn } from '@/lib/utils';

import { getWrapperMaxWidth } from '@/cms/utils';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { BannerContent } from './BannerContent';
import {
  BannerContentProps,
  TopBannerColorScheme,
  TopBannerProps,
  TopBannerStrapiProps,
} from './types';

import { getBannerColorScheme } from '@/blocks/topBanner/helpers';
import { SvgIcon } from '@/components/SvgIcon';
import iconCloseSrc from '~/icons/x-close.svg';

const BANNER_DISMISS_KEY = 'topBannerDismissed';
const defaultColorScheme: TopBannerColorScheme = 'brand-dark';

type UnifiedTopBannerProps = {
  top_banner_elements?: TopBannerStrapiProps['top_banner_elements'];
  bannerContent?: TopBannerProps['bannerContent'];
  colorScheme?: TopBannerColorScheme;
};

function normalizeBannerContent(content?: BannerContentProps[]) {
  if (Array.isArray(content)) return content.filter(Boolean);
  if (content) return [content];
  return [];
}

export const TopBanner = ({
  top_banner_elements,
  bannerContent,
  colorScheme = defaultColorScheme,
}: UnifiedTopBannerProps) => {
  const [isTopBannerOpen, setIsTopBannerOpen] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const multipleBanners = useMemo(() => {
    if (Array.isArray(top_banner_elements?.data)) {
      const rawContent = top_banner_elements.data.map(
        (el) => el.attributes?.bannerContent
      );
      return normalizeBannerContent(rawContent);
    }

    return normalizeBannerContent(bannerContent);
  }, [top_banner_elements, bannerContent]);

  const hasEndDate = multipleBanners.some((banner) => !!banner.endDate);
  const tabletDesktopHeightClass = hasEndDate ? 'sm:h-[48px]' : 'sm:h-[40px]';

  useLayoutEffect(() => {
    // Clean up the CSS-only pre-hydration attribute; React state takes over from here.
    delete document.documentElement.dataset.bannerDismissed;

    const dismissedAt = localStorage.getItem(BANNER_DISMISS_KEY);
    if (!dismissedAt) return;

    const dismissedTime = parseInt(dismissedAt, 10);
    const oneDay = 24 * 60 * 60 * 1000;

    if (isNaN(dismissedTime) || Date.now() - dismissedTime >= oneDay) {
      localStorage.removeItem(BANNER_DISMISS_KEY);
      return;
    }

    document.documentElement.style.setProperty('--banner-offset', '0px');
    setIsTopBannerOpen(false);
  }, []);

  useEffect(() => {
    if (!isTopBannerOpen) return;

    const interval = setInterval(() => {
      if (!isHovered) {
        setCurrentIndex((prev) => (prev + 1) % multipleBanners.length);
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [isTopBannerOpen, isHovered, multipleBanners.length]);

  const handleClose = () => {
    document.documentElement.style.setProperty('--banner-offset', '0px');
    setIsTopBannerOpen(false);
    localStorage.setItem(BANNER_DISMISS_KEY, Date.now().toString());
  };

  if (!isTopBannerOpen) return null;
  if (multipleBanners.length === 0) return null;

  return (
    <div
      id='top-banner'
      data-tracker-section={SECTION_NAMES.TOP_BANNER}
      className={cn(
        'light',
        'bg-base-white',
        'headerLg:justify-center top-0 fixed z-[60] w-full h-[72px] justify-end shadow-sm m:flex',
        tabletDesktopHeightClass
      )}
    >
      {/*
        This container is necessary, otherwise the semi-transparent gradient will show content underneath. 
       */}
      <div
        className={cn(
          'headerLg:justify-center headerLg:px-10 mx-auto w-full h-full justify-end px-5 py-3 sm:py-0 shadow-sm md:flex',
          getBannerColorScheme(colorScheme).bannerBg,
          bannerStyles.banner
        )}
      >
        <div
          className={cn(
            'relative flex h-full min-h-[52px] sm:min-h-0 w-full grow items-center justify-end',
            tabletDesktopHeightClass,
            getWrapperMaxWidth('contained')
          )}
        >
          <div
            key={currentIndex}
            className='absolute inset-0 flex items-center justify-center pr-10 sm:pr-0'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <BannerContent
              {...multipleBanners[currentIndex]}
              colorScheme={colorScheme}
            />
          </div>
          <div
            className='absolute top-0 right-0 z-10 cursor-pointer sm:static sm:ml-6 xl:ml-[110px] sm:flex sm:items-center'
            onClick={handleClose}
            data-tracker='close-promo-banner'
          >
            <SvgIcon
              src={iconCloseSrc}
              className={cn('h-6 w-6', getBannerColorScheme(colorScheme).text)}
              aria-hidden
            />
          </div>
        </div>
      </div>
    </div>
  );
};
